import React, { useEffect } from 'react';
import { Button } from './ui/Button';
import { Check, Shield, X, AlertTriangle, ArrowRight } from 'lucide-react';
interface ExitUpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}
export function ExitUpsellPopup({
  isOpen,
  onClose,
  onAccept,
  onDecline
}: ExitUpsellPopupProps) {
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-45">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} data-id="element-46" />

      {/* Styles for animations */}
      <style data-id="element-47">{`
        @keyframes flash-badge {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes pulse-glow {
          0% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(217, 119, 6, 0); }
          100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
        }
        .animate-flash { animation: flash-badge 2s infinite ease-in-out; }
        .animate-pulse-glow { animation: pulse-glow 2s infinite; }
      `}</style>

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100 opacity-100 max-h-[90vh] overflow-y-auto" data-id="element-48">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-3 right-3 text-stone-400 hover:text-white z-10 transition-colors bg-black/20 hover:bg-black/40 rounded-full p-1" data-id="element-49">
          <X className="w-5 h-5" data-id="element-50" />
        </button>

        {/* Header */}
        <div className="bg-stone-900 p-6 text-center relative overflow-hidden" data-id="element-51">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" data-id="element-52"></div>

          <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border border-amber-200 shadow-sm" data-id="element-53">
            <ClockIcon className="w-3 h-3" data-id="element-54" />
            Oferta única en esta pantalla
          </div>

          <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2 leading-tight" data-id="element-55">
            🚨 ¡Espera! No te vayas sin el Paquete Completo
          </h2>
          <p className="text-amber-400 font-medium text-sm md:text-base" data-id="element-56">
            Llévate TODO lo que necesitas para dominar el Mil Hojas perfecto
          </p>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8" data-id="element-57">
          {/* Flashing Badge */}
          <div className="flex justify-center mb-6" data-id="element-58">
            <div className="animate-flash bg-red-600 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg border-2 border-white ring-2 ring-red-200 flex items-center gap-2" data-id="element-59">
              <span className="text-lg" data-id="element-60">🔥</span> 30% OFF en los extras
            </div>
          </div>

          {/* Main Copy */}
          <div className="text-center mb-6 space-y-3" data-id="element-61">
            <p className="text-stone-700 leading-relaxed" data-id="element-62">
              Con la guía sola aprenderás la técnica base. Pero con el{' '}
              <span className="font-bold text-stone-900" data-id="element-63">Paquete Completo</span>
              , tendrás los rellenos profesionales que no humedecen tu hojaldre
              Y las medidas exactas para nunca desperdiciar ingredientes.
            </p>
            <p className="text-stone-500 text-sm italic" data-id="element-64">
              Es la diferencia entre un Mil Hojas bueno y uno de pastelería
              profesional.
            </p>
          </div>

          {/* What's Included */}
          <div className="bg-stone-50 rounded-xl p-5 border border-stone-200 mb-6" data-id="element-65">
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3 text-center" data-id="element-66">
              Lo que te llevas hoy:
            </h3>
            <ul className="space-y-2.5" data-id="element-67">
              <ListItem text="Guía Maestra Mil Hojas Pro (PDF)" price="$9.00" data-id="element-68" />
              <ListItem text="Checklist Anti-Humedad" price="Gratis" highlight data-id="element-69" />
              <ListItem text='Guía "Corte Limpio"' price="Gratis" highlight data-id="element-70" />
              <ListItem text="Kit Solución de Problemas" price="Gratis" highlight data-id="element-71" />
              <ListItem text="Pack Rellenos 5 Minutos" price="$7.00" discount data-id="element-72" />
              <ListItem text="Planner de Porciones & Medidas" price="$7.00" discount data-id="element-73" />
            </ul>
          </div>

          {/* Pricing */}
          <div className="text-center mb-8" data-id="element-74">
            <div className="flex items-center justify-center gap-3 mb-1" data-id="element-75">
              <span className="text-lg text-stone-400 line-through decoration-red-400 decoration-2" data-id="element-76">
                $23.00
              </span>
              <span className="text-4xl font-bold text-stone-900" data-id="element-77">
                $18.80{' '}
                <span className="text-xl text-stone-500 font-medium" data-id="element-78">USD</span>
              </span>
            </div>

            <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-79">
              Ahorras $4.20 hoy
            </div>

            <p className="text-xs text-stone-500 mb-4" data-id="element-80">
              Precio especial solo disponible en esta pantalla
            </p>

            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400" data-id="element-81">
              <Shield className="w-3 h-3" data-id="element-82" />
              Pago 100% Seguro y Encriptado
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-3" data-id="element-83">
            <Button variant="primary" size="lg" fullWidth onClick={onAccept} className="animate-pulse-glow text-base md:text-lg py-6 shadow-xl shadow-amber-200" data-id="element-84">
              SÍ, QUIERO EL PAQUETE COMPLETO POR $18.80
            </Button>

            <button onClick={onDecline} className="w-full text-center text-xs text-stone-400 hover:text-stone-600 underline decoration-stone-300 hover:decoration-stone-500 transition-colors py-2" data-id="element-85">
              No gracias, continuar sin los extras →
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function ListItem({
  text,
  price,
  highlight = false,
  discount = false
}: {
  text: string;
  price: string;
  highlight?: boolean;
  discount?: boolean;
}) {
  return <li className="flex items-start justify-between gap-2 text-sm" data-id="element-86">
      <div className="flex items-start gap-2" data-id="element-87">
        <div className={`mt-0.5 rounded-full p-0.5 ${highlight ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-88">
          <Check className="w-3 h-3" strokeWidth={3} data-id="element-89" />
        </div>
        <span className="text-stone-700 font-medium" data-id="element-90">{text}</span>
      </div>
      <div className="flex flex-col items-end" data-id="element-91">
        {discount && <span className="text-[10px] text-red-500 font-bold leading-none mb-0.5" data-id="element-92">
            30% OFF
          </span>}
        <span className={`font-bold whitespace-nowrap ${highlight ? 'text-green-600' : 'text-stone-900'}`} data-id="element-93">
          {price}
        </span>
      </div>
    </li>;
}
function ClockIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-94">
      <circle cx="12" cy="12" r="10" data-id="element-95" />
      <polyline points="12 6 12 12 16 14" data-id="element-96" />
    </svg>;
}